import axios from 'axios';
import { API_URL } from '../config/api';
import SessionService from './SessionService';

/** E-mail na linha do tempo do cliente (texto puro; o HTML original nunca é renderizado na conversa). */
export interface ContactEmailItem {
  id: number;
  direction: 'in' | 'out';
  subject: string | null;
  from: string;
  to: string;
  cc: string | null;
  at: string;
  text: string;
  threadId: string | null;
  folder: string;
  sentFromConversation: boolean;
}

export interface ContactEmailList {
  items: ContactEmailItem[];
  total: number;
  hasMore: boolean;
}

export interface ContactEmailStatus {
  connected: boolean;
  provider: string | null;
  mailbox: string | null;
  contactEmail: string | null;
  canReply: boolean;
  reason: string | null;
  lastSyncedAt: string | null;
  localSafeMode: boolean;
}

export interface ContactEmailReplyInput {
  body: string;
  subject?: string;
  replyToEmailId?: number | null;
  /** Obrigatório no envio: a impressão devolvida pela pré-visualização (409 se o e-mail mudou desde então). */
  draftFingerprint?: string;
  /** Gerada por rascunho no navegador: a mesma chave nunca envia duas vezes. */
  idempotencyKey?: string;
}

/** O e-mail exatamente como vai sair. */
export interface ContactEmailDraft {
  from: string;
  to: string;
  subject: string;
  bodyText: string;
  bodyHtml: string;
  quotedText: string | null;
  replyToEmailId: number | null;
  /** Impressão do rascunho (e-mail respondido, caixa, destinatário, assunto e corpo); o envio só sai com ela. */
  fingerprint: string;
}

export interface ContactEmailReplyResult {
  item: ContactEmailItem;
  captured: boolean;
}

function headers() {
  const token = SessionService.getToken();
  return { Accept: 'application/json', 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
}

const base = (contactId: number) => `${API_URL}/Contact/${contactId}/emails`;

export { contactEmailErrorMessage, isEmailForbidden } from './contactEmailErrors';

/** Quantos e-mails a conversa carrega de início e a cada "carregar anteriores". */
export const CONTACT_EMAIL_PAGE_SIZE = 50;
/** Teto do servidor por pedido (ContactEmailConversationService.ListAsync). */
export const CONTACT_EMAIL_MAX_LIMIT = 200;

export async function getContactEmails(sectorId: number, contactId: number, limit = CONTACT_EMAIL_PAGE_SIZE): Promise<ContactEmailList> {
  const res = await axios.get<ContactEmailList>(base(contactId), { params: { sectorId, limit }, headers: headers() });
  return res.data;
}

export async function getContactEmailStatus(sectorId: number, contactId: number): Promise<ContactEmailStatus> {
  const res = await axios.get<ContactEmailStatus>(`${base(contactId)}/status`, { params: { sectorId }, headers: headers() });
  return res.data;
}

export async function previewContactEmailReply(sectorId: number, contactId: number, input: ContactEmailReplyInput): Promise<ContactEmailDraft> {
  const res = await axios.post<ContactEmailDraft>(`${base(contactId)}/preview`, { sectorId, ...input }, { headers: headers() });
  return res.data;
}

export async function sendContactEmailReply(sectorId: number, contactId: number, input: ContactEmailReplyInput): Promise<ContactEmailReplyResult> {
  const res = await axios.post<ContactEmailReplyResult>(`${base(contactId)}/reply`, { sectorId, ...input }, { headers: headers() });
  return res.data;
}
